(() => {
  const t = (key, fallback, variables = {}) => window.nawahTranslate?.(key, fallback, variables) || fallback || key;
  const SELECTORS = {
    quickViewButton: '[data-nawah-quick-view]',
    quickAddButton: '[data-nawah-quick-add]',
    modal: '[data-nawah-quick-view-modal]',
    modalContent: '[data-nawah-qv-content]',
    modalLoading: '[data-nawah-qv-loading]',
    recommendations: '[data-nawah-cart-recommendations]',
    utilityRoot: '[data-nawah-utility-drawers]',
    utilityPanel: '[data-nawah-drawer-panel]',
    searchTrigger: '[data-nawah-search-trigger]',
    accountTrigger: '[data-nawah-account-trigger]',
  };

  const state = {
    modalTrigger: null,
    utilityTrigger: null,
    utilityName: '',
    searchTimer: null,
    searchController: null,
  };

  async function readJsonResponse(response, fallbackMessage) {
    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch (error) {
      console.error('Unexpected Shopify response:', text.slice(0, 300));
      throw new Error(fallbackMessage || `Unexpected response (${response.status})`);
    }
  }

  function escapeHtml(value = '') {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function toast(message) {
    let node = document.querySelector('.nawah-toast');
    if (!node) {
      node = document.createElement('div');
      node.className = 'nawah-toast';
      node.setAttribute('role', 'status');
      node.setAttribute('aria-live', 'polite');
      document.body.appendChild(node);
    }
    node.textContent = message;
    node.classList.add('is-visible');
    clearTimeout(node._timer);
    node._timer = setTimeout(() => node.classList.remove('is-visible'), 2600);
  }

  function cartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function isCartOpen() {
    return cartDrawer()?.classList.contains('active') || false;
  }

  function utilityRoot() {
    return document.querySelector(SELECTORS.utilityRoot);
  }

  function isUtilityOpen() {
    const root = utilityRoot();
    return Boolean(root && !root.hidden);
  }

  function syncBodyScrollLock() {
    const quickViewOpen = Boolean(modal() && !modal().hidden);
    document.body.classList.toggle('overflow-hidden', quickViewOpen || isUtilityOpen() || isCartOpen());
  }

  function closeHeaderMenu() {
    const header = document.querySelector('nawah-header');
    if (header && typeof header.closeMenu === 'function') header.closeMenu();
  }

  function setDrawerNavState(name, active) {
    const selector = name === 'search'
      ? SELECTORS.searchTrigger
      : name === 'account'
        ? SELECTORS.accountTrigger
        : '';
    if (!selector) return;
    document.querySelectorAll(selector).forEach((element) => {
      if (element.closest('.nawah-mobile-nav')) element.classList.toggle('is-drawer-active', active);
    });
  }

  function openUtilityDrawer(name, trigger) {
    const root = utilityRoot();
    const panel = root?.querySelector(`[data-nawah-drawer-panel="${name}"]`);
    if (!root || !panel) return;

    const menuFallback = trigger?.closest?.('.nawah-mobile-menu') ? document.querySelector('.nawah-header__menu-toggle') : null;
    closeHeaderMenu();
    if (state.utilityName) setDrawerNavState(state.utilityName, false);
    state.utilityTrigger = menuFallback || trigger || document.activeElement;
    state.utilityName = name;

    root.querySelectorAll(SELECTORS.utilityPanel).forEach((item) => {
      item.hidden = item !== panel;
      item.classList.toggle('is-active', item === panel);
    });
    root.hidden = false;
    root.dataset.activeDrawer = name;
    setDrawerNavState(name, true);
    syncBodyScrollLock();

    requestAnimationFrame(() => {
      const focusTarget = name === 'search'
        ? panel.querySelector('[data-nawah-search-input]')
        : panel.querySelector('a, button, input, select, [tabindex]:not([tabindex="-1"])');
      if (typeof trapFocus === 'function') trapFocus(panel, focusTarget || panel);
      else (focusTarget || panel).focus?.({ preventScroll: true });
    });
  }

  function closeUtilityDrawer({ restoreFocus = true } = {}) {
    const root = utilityRoot();
    if (!root || root.hidden) return;
    const previousName = state.utilityName;
    const previousTrigger = state.utilityTrigger;

    root.hidden = true;
    root.removeAttribute('data-active-drawer');
    root.querySelectorAll(SELECTORS.utilityPanel).forEach((panel) => {
      panel.hidden = true;
      panel.classList.remove('is-active');
    });
    setDrawerNavState(previousName, false);
    state.utilityName = '';
    state.utilityTrigger = null;

    if (typeof removeTrapFocus === 'function') removeTrapFocus(restoreFocus ? previousTrigger : null);
    else if (restoreFocus) previousTrigger?.focus?.({ preventScroll: true });
    syncBodyScrollLock();
  }

  function formatMoney(cents) {
    const format = window.nawahCommerce?.moneyWithCurrencyFormat || window.nawahCommerce?.moneyFormat;
    const value = Number(cents || 0);

    const formatWithDelimiters = (amount, precision = 2, thousands = ',', decimal = '.') => {
      if (!Number.isFinite(amount)) return '0';
      const fixed = (amount / 100).toFixed(precision);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? `${whole}${decimal}${parts[1]}` : whole;
    };

    if (format && format.includes('{{')) {
      const match = format.match(/{{\s*(\w+)\s*}}/);
      const placeholder = match?.[1] || 'amount';
      const replacements = {
        amount: formatWithDelimiters(value, 2, ',', '.'),
        amount_no_decimals: formatWithDelimiters(value, 0, ',', '.'),
        amount_with_comma_separator: formatWithDelimiters(value, 2, '.', ','),
        amount_no_decimals_with_comma_separator: formatWithDelimiters(value, 0, '.', ','),
        amount_with_apostrophe_separator: formatWithDelimiters(value, 2, "'", '.'),
      };
      return format.replace(match[0], replacements[placeholder] || replacements.amount);
    }

    const currency = window.nawahCommerce?.currency || window.Shopify?.currency?.active || 'EGP';
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(value / 100);
  }

  function formatPredictivePrice(value) {
    const raw = String(value ?? '').trim();
    const number = Number(raw.replace(/[^0-9.-]/g, ''));
    if (!Number.isFinite(number)) return escapeHtml(value || '');
    const looksLikeCents = typeof value === 'number' && Number.isInteger(value) && Math.abs(value) > 10000;
    return formatMoney(looksLikeCents ? number : Math.round(number * 100));
  }

  function initSearchDrawer() {
    const root = utilityRoot();
    const input = root?.querySelector('[data-nawah-search-input]');
    const clear = root?.querySelector('[data-nawah-search-clear]');
    const form = root?.querySelector('[data-nawah-search-form]');
    const category = root?.querySelector('[data-nawah-search-category]');
    const results = root?.querySelector('[data-nawah-search-results]');
    const suggestions = root?.querySelector('[data-nawah-search-suggestions]');
    const list = root?.querySelector('[data-nawah-search-result-list]');
    const status = root?.querySelector('[data-nawah-search-status]');
    const viewAll = root?.querySelector('[data-nawah-search-view-all]');
    if (!input || input.dataset.initialized === 'true') return;
    input.dataset.initialized = 'true';

    const reset = () => {
      state.searchController?.abort();
      if (list) list.innerHTML = '';
      if (results) results.hidden = true;
      if (suggestions) suggestions.hidden = false;
      if (status) status.textContent = '';
      if (clear) clear.hidden = true;
    };

    const renderProducts = (products, query) => {
      if (!list || !results || !suggestions) return;
      list.innerHTML = products.map((product) => {
        const image = product.featured_image?.url || product.featured_image || product.image || '';
        const compare = Number(product.compare_at_price_max || product.compare_at_price || 0);
        const price = Number(product.price || product.price_min || 0);
        return `
          <a class="nawah-search-result" href="${escapeHtml(product.url || '#')}">
            <span class="nawah-search-result__media">${image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy">` : ''}</span>
            <span class="nawah-search-result__copy">
              <strong>${escapeHtml(product.title || '')}</strong>
              <span>${compare > price ? `<s>${formatPredictivePrice(compare)}</s>` : ''}<b>${formatPredictivePrice(price)}</b></span>
            </span>
            <span aria-hidden="true">→</span>
          </a>`;
      }).join('');
      results.hidden = false;
      suggestions.hidden = true;
      if (viewAll) viewAll.href = `${window.routes?.search_url || '/search'}?type=product&q=${encodeURIComponent(query)}`;
      if (status) status.textContent = products.length ? t('suggestions', '{{ count }} suggestions', { count: products.length }) : t('no_matching_products', 'No matching products found.');
      if (!products.length) list.innerHTML = `<p class="nawah-search-drawer__empty">${escapeHtml(t('no_matching_products_help', 'No matching products yet. Try another product name or category.'))}</p>`;
    };

    const search = async (query) => {
      state.searchController?.abort();
      state.searchController = new AbortController();
      if (status) status.textContent = t('searching', 'Searching…');
      try {
        const base = window.routes?.predictive_search_url || '/search/suggest';
        const endpoint = base.endsWith('.json') ? base : `${base}.json`;
        const url = new URL(endpoint, window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        const response = await fetch(url.toString(), {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          signal: state.searchController.signal,
        });
        if (!response.ok) throw new Error(`Search request failed (${response.status})`);
        const data = await response.json();
        renderProducts(data?.resources?.results?.products || [], query);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error(error);
        if (status) status.textContent = t('search_unavailable', 'Search is temporarily unavailable.');
      }
    };

    input.addEventListener('input', () => {
      const query = input.value.trim();
      if (clear) clear.hidden = query.length === 0;
      clearTimeout(state.searchTimer);
      if (query.length < 2) {
        reset();
        return;
      }
      state.searchTimer = setTimeout(() => search(query), 220);
    });

    clear?.addEventListener('click', () => {
      input.value = '';
      reset();
      input.focus();
    });

    form?.addEventListener('submit', (event) => {
      if (!input.value.trim() && category?.value) {
        event.preventDefault();
        window.location.href = category.value;
      }
    });
  }

  function modal() {
    return document.querySelector(SELECTORS.modal);
  }

  async function openQuickView(handle, trigger) {
    const root = modal();
    if (!root || !handle) return;
    state.modalTrigger = trigger || document.activeElement;
    const content = root.querySelector(SELECTORS.modalContent);
    const loading = root.querySelector(SELECTORS.modalLoading);
    content.innerHTML = '';
    content.hidden = true;
    loading.hidden = false;
    root.hidden = false;
    root.classList.toggle('is-over-cart', isCartOpen());
    root.setAttribute('aria-busy', 'true');
    document.body.classList.add('nawah-quick-view-open');
    syncBodyScrollLock();
    const dialog = root.querySelector('.nawah-qv__dialog');
    dialog?.focus();
    if (typeof trapFocus === 'function' && dialog) trapFocus(dialog, root.querySelector('[data-nawah-qv-close]') || dialog);

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 10000);

    try {
      const response = await fetch(`/products/${encodeURIComponent(handle)}?section_id=nawah-quick-view-content`, {
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Quick view request failed (${response.status})`);
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const productNode = parsed.querySelector('[data-nawah-qv-product]');
      if (!productNode) throw new Error(t('quick_view_error', 'Quick view content was not returned by Shopify.'));
      content.innerHTML = productNode.outerHTML;
      content.hidden = false;
      initQuickViewContent(content);
    } catch (error) {
      console.error(error);
      content.hidden = false;
      content.innerHTML = `<div class="nawah-qv-product__info"><h2>${escapeHtml(t('unable_to_load_product', 'Unable to load product'))}</h2><p>${escapeHtml(t('close_and_try_again', 'Please close this window and try again.'))}</p></div>`;
    } finally {
      window.clearTimeout(timeout);
      loading.hidden = true;
      root.removeAttribute('aria-busy');
    }
  }

  function closeQuickView() {
    const root = modal();
    if (!root || root.hidden) return;
    const trigger = state.modalTrigger;
    root.hidden = true;
    root.classList.remove('is-over-cart');
    root.querySelector(SELECTORS.modalContent).innerHTML = '';
    document.body.classList.remove('nawah-quick-view-open');

    if (isCartOpen()) {
      const drawerInner = cartDrawer()?.querySelector('.drawer__inner');
      if (drawerInner && typeof trapFocus === 'function') trapFocus(drawerInner, trigger || drawerInner);
      else trigger?.focus?.({ preventScroll: true });
    } else if (isUtilityOpen()) {
      const panel = utilityRoot()?.querySelector(`${SELECTORS.utilityPanel}:not([hidden])`);
      if (panel && typeof trapFocus === 'function') trapFocus(panel, trigger || panel);
      else trigger?.focus?.({ preventScroll: true });
    } else if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(trigger);
    } else {
      trigger?.focus?.({ preventScroll: true });
    }
    state.modalTrigger = null;
    syncBodyScrollLock();
  }

  function initQuickViewContent(scope) {
    const select = scope.querySelector('[data-qv-variant-select]');
    const variantInput = scope.querySelector('[data-qv-variant-input]');
    const quantityInput = scope.querySelector('[data-qv-qty-input]');
    const formQuantity = scope.querySelector('[data-qv-form-quantity]');
    const addButton = scope.querySelector('[data-qv-add]');
    const addLabel = scope.querySelector('[data-qv-add-label]');
    const availability = scope.querySelector('[data-qv-availability]');

    const syncQuantity = () => {
      const value = Math.max(1, Number(quantityInput?.value || 1));
      if (quantityInput) quantityInput.value = value;
      if (formQuantity) formQuantity.value = value;
    };

    scope.querySelector('[data-qv-qty-minus]')?.addEventListener('click', () => {
      if (quantityInput) quantityInput.value = Math.max(1, Number(quantityInput.value || 1) - 1);
      syncQuantity();
    });
    scope.querySelector('[data-qv-qty-plus]')?.addEventListener('click', () => {
      if (quantityInput) quantityInput.value = Number(quantityInput.value || 1) + 1;
      syncQuantity();
    });
    quantityInput?.addEventListener('change', syncQuantity);

    select?.addEventListener('change', () => {
      const option = select.selectedOptions[0];
      const available = option.dataset.available === 'true';
      if (variantInput) variantInput.value = option.value;
      if (addButton) addButton.disabled = !available;
      if (addLabel) addLabel.textContent = available ? t('add_to_cart', 'Add to cart') : t('sold_out', 'Sold out');
      if (availability) availability.textContent = available ? t('in_stock', 'In stock') : t('sold_out', 'Sold out');
      const current = scope.querySelector('.price__regular .price-item--regular, .price__sale .price-item--sale');
      if (current) current.textContent = formatMoney(option.dataset.price);
      const compare = scope.querySelector('.price__sale s.price-item--regular');
      if (compare) {
        const compareCents = Number(option.dataset.comparePrice || 0);
        compare.textContent = compareCents > Number(option.dataset.price) ? formatMoney(compareCents) : '';
      }
    });
  }

  async function fetchCartSections() {
    const sectionIds = ['cart-drawer', 'cart-icon-bubble'];
    const url = new URL(window.location.href);
    url.searchParams.set('sections', sectionIds.join(','));
    const response = await fetch(url.toString(), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Cart sections request failed (${response.status})`);
    return response.json();
  }

  async function quickAdd(button) {
    if (button.dataset.requiresGiftChoice === 'true') {
      window.location.assign(button.dataset.productUrl || `/products/${button.dataset.productHandle}`);
      return;
    }

    if (button.dataset.hasVariants === 'true') {
      openQuickView(button.dataset.productHandle, button);
      return;
    }

    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    button.classList.add('is-loading');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const drawer = cartDrawer();
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      if (drawer?.getSectionsToRender) {
        formData.append('sections', drawer.getSectionsToRender().map((section) => section.id).join(','));
        formData.append('sections_url', `${window.location.pathname}${window.location.search}`);
        drawer.setActiveElement?.(button);
      }

      const config = typeof fetchConfig === 'function'
        ? fetchConfig('javascript')
        : { method: 'POST', headers: { Accept: 'application/json' } };
      config.headers = config.headers || {};
      config.headers.Accept = 'application/json';
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;
      config.credentials = 'same-origin';

      const addUrl = window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add`;
      const response = await fetch(addUrl, config);
      const data = await readJsonResponse(response, t('add_error', 'Shopify could not add this product. Please try again.'));
      if (!response.ok || data.status) throw new Error(data.description || data.message || t('unable_to_add_product', 'Unable to add product'));

      if (drawer) {
        if (!data.sections?.['cart-drawer']) data.sections = await fetchCartSections();
        drawer.classList.remove('is-empty');
        drawer.querySelector('cart-drawer-items')?.classList.remove('is-empty');

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          try {
            await publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'nawah-quick-add',
              productVariantId: variantId,
              cartData: data,
            });
          } catch (error) {
            console.warn('NAWAH cart update subscribers did not complete', error);
          }
        }

        drawer.renderContents(data);
        window.setTimeout(() => prepareCartRecommendations(document.querySelector('cart-drawer') || document), 120);
      } else {
        window.location.href = window.routes?.cart_url || '/cart';
        return;
      }

      await refreshCartCount();
      toast(t('added_to_cart', 'Added to cart'));
    } catch (error) {
      console.error(error);
      toast(error.message || t('unable_to_add', 'Unable to add product'));
    } finally {
      button.classList.remove('is-loading');
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  async function refreshCartCount() {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      document.querySelectorAll('[data-nawah-cart-count]').forEach((count) => {
        count.textContent = cart.item_count;
        count.hidden = cart.item_count === 0;
      });
    } catch (error) {
      console.warn('Unable to refresh cart count', error);
    }
  }

  function prepareCartRecommendations(scope = document) {
    const root = scope?.querySelector?.(SELECTORS.recommendations) || document.querySelector(SELECTORS.recommendations);
    if (!root) return;
    const track = root.querySelector('[data-nawah-recs-track]');
    const controls = root.querySelector('[data-nawah-recs-controls]');
    const itemCount = track?.children.length || 0;
    root.dataset.loaded = 'true';
    root.dataset.recIndex = '0';
    if (controls) controls.hidden = itemCount <= 1;
    updateRecommendationPosition(root);
  }

  function updateRecommendationPosition(root) {
    const track = root?.querySelector('[data-nawah-recs-track]');
    if (!track) return;
    const items = track.children.length;
    const current = Number(root.dataset.recIndex || 0);
    const nextIndex = Math.max(0, Math.min(current, Math.max(0, items - 1)));
    root.dataset.recIndex = String(nextIndex);
    track.style.transform = `translateX(${document.documentElement.dir === 'rtl' ? '' : '-'}${nextIndex * 100}%)`;
    const previous = root.querySelector('[data-nawah-recs-prev]');
    const next = root.querySelector('[data-nawah-recs-next]');
    if (previous) previous.disabled = nextIndex === 0;
    if (next) next.disabled = nextIndex >= items - 1;
  }

  function bindGlobalEvents() {
    document.addEventListener('click', (event) => {
      const searchTrigger = event.target.closest(SELECTORS.searchTrigger);
      if (searchTrigger) {
        event.preventDefault();
        event.stopPropagation();
        openUtilityDrawer('search', searchTrigger);
        return;
      }

      const accountTrigger = event.target.closest(SELECTORS.accountTrigger);
      if (accountTrigger) {
        event.preventDefault();
        event.stopPropagation();
        openUtilityDrawer('account', accountTrigger);
        return;
      }

      if (event.target.closest('[data-nawah-utility-close]')) {
        event.preventDefault();
        closeUtilityDrawer();
        return;
      }

      const quickViewButton = event.target.closest(SELECTORS.quickViewButton);
      if (quickViewButton) {
        event.preventDefault();
        event.stopPropagation();
        openQuickView(quickViewButton.dataset.productHandle, quickViewButton);
        return;
      }

      const quickAddButton = event.target.closest(SELECTORS.quickAddButton);
      if (quickAddButton) {
        event.preventDefault();
        event.stopPropagation();
        quickAdd(quickAddButton);
        return;
      }

      if (event.target.closest('[data-nawah-qv-close]')) {
        event.preventDefault();
        closeQuickView();
        return;
      }

      const cartTrigger = event.target.closest('[data-nawah-cart-trigger]');
      const drawer = cartDrawer();
      if (cartTrigger && drawer) {
        event.preventDefault();
        drawer.open(cartTrigger);
        return;
      }

      const previous = event.target.closest('[data-nawah-recs-prev]');
      const next = event.target.closest('[data-nawah-recs-next]');
      if (previous || next) {
        event.preventDefault();
        const recommendations = event.target.closest(SELECTORS.recommendations);
        const items = recommendations?.querySelector('[data-nawah-recs-track]')?.children.length || 0;
        const current = Number(recommendations?.dataset.recIndex || 0);
        const nextIndex = previous ? Math.max(0, current - 1) : Math.min(Math.max(0, items - 1), current + 1);
        if (recommendations) recommendations.dataset.recIndex = String(nextIndex);
        updateRecommendationPosition(recommendations);
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (modal() && !modal().hidden) closeQuickView();
      else if (isUtilityOpen()) closeUtilityDrawer();
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try {
        subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          setTimeout(() => {
            refreshCartCount();
            prepareCartRecommendations();
            closeQuickView();
          }, 80);
        });
      } catch (error) {
        console.warn('NAWAH commerce cart subscription unavailable', error);
      }
    }

    document.addEventListener('shopify:section:load', (event) => {
      prepareCartRecommendations(event.target);
      initSearchDrawer();
    });
  }

  function init() {
    bindGlobalEvents();
    initSearchDrawer();
    refreshCartCount();
    prepareCartRecommendations();

    const drawer = cartDrawer();
    if (drawer) {
      const observer = new MutationObserver(() => {
        const recommendations = drawer.querySelector(SELECTORS.recommendations);
        if (recommendations) prepareCartRecommendations(drawer);
        syncBodyScrollLock();
      });
      observer.observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
